// @ts-check
/**
 * @typedef {Object} FfCartDrawerCustomProperties
 * @property {(enable: boolean) => void} enable If true removes disable attribute
 * @property {() => Promise<void>} ffInit Adds click event for submit and close drawer
 * @property {() => {id: string}[]} getSectionsToRender
 * @property {(parsedState:{sections:{[sectionID:string]: string}|null}) => Promise<void>} renderContents
 *
 * @typedef {FfCartDrawerCustomProperties & FfDrawer} FfCartDrawer
 */
if (!customElements.get("cart-drawer")) {
  class FfRecommendationCard extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      /**
       * @type {HTMLButtonElement}
       */
      // @ts-ignore
      const addToCartBtn = this.querySelector(
        "[data-ff-recommendation-add-to-cart]"
      );

      addToCartBtn.addEventListener("click", () =>
        this.addToCart(addToCartBtn)
      );
    }

    /**
     *
     * @param {HTMLButtonElement} addToCartBtn
     */
    async addToCart(addToCartBtn) {
      const spinner = ffGetSpinner();
      addToCartBtn?.appendChild(spinner);
      addToCartBtn?.classList.add("ff-loading");
      addToCartBtn?.setAttribute("disabled", "disabled");

      /**
       * @type {FfCartDrawer}
       */
      // @ts-ignore
      const cartDrawer = document.querySelector("cart-drawer");
      cartDrawer.enable(false);

      const requestData = {
        items: [
          // @ts-ignore
          { id: parseInt(addToCartBtn.dataset.variantId), quantity: 1 },
        ],
        sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      };

      await fetch(`${window["Shopify"].routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((response) => {
          return response.json().then((data) => {
            cartDrawer.renderContents(data);

            return data;
          });
        })
        .catch((error) => {
          console.error("Error:", error);
          throw error;
        });

      ffReleaseSpinner(spinner);
      addToCartBtn.classList.remove("ff-loading");
      addToCartBtn.removeAttribute("disabled");
      this.remove();
      cartDrawer.enable(true);
      if (cartDrawer.querySelectorAll("ff-recommendation-card").length == 0) {
        cartDrawer
          .querySelector("#ff-cart-drawer_recommendations")
          .classList.add("ff-d-none");
      }
    }
  }

  customElements.define("ff-recommendation-card", FfRecommendationCard);

  customElements.define(
    "cart-drawer",
    class FfCartDrawer extends FfDrawer {
      static crossSelling = {
        maxOriginProducts: 4, //products which originate the recommendations
        maxRetrievedProducts: 4, //products retrieved per recommendation fetch
        maxReturnedRecommendations: 10, //total products from all recommendations fetch
      };

      constructor() {
        super();

        //Up cross selling element doesn't exist when the cart is empty
        if (this.querySelector("[data-ff-line-item]")) {
          this.ffInitCrossSelling();
        }
      }

      async ffInitCrossSelling() {
        const {
          maxOriginProducts,
          maxRetrievedProducts,
          maxReturnedRecommendations,
        } = FfCartDrawer.crossSelling;
        const promises = [];
        let allRecommendations = [];
        /**
         * @type {HTMLDivElement}
         */
        // @ts-ignore
        const crossSellingEl = this.querySelector(
          "#ff-cart-drawer_recommendations"
        );

        const productsIds = Array.from(
          new Set(crossSellingEl?.dataset.productsIds?.split(","))
        );

        for (let i = 0; i < productsIds.length && i < maxOriginProducts; i++) {
          const url = `${crossSellingEl.dataset.url}?product_id=${productsIds[i]}&intent=complementary&limit=${maxRetrievedProducts}`;

          const promise = (async () => {
            const response = await fetch(url);
            const { products } = await response.json();
            allRecommendations = [...allRecommendations, ...products];
          })();

          promises.push(promise);
        }

        await Promise.allSettled(promises);

        allRecommendations
          .reduce((acc, curr) => {
            if (!acc.find((item) => item.id === curr.id)) {
              acc.push(curr);
            }
            return acc;
          }, [])
          .slice(0, maxReturnedRecommendations)
          .forEach((product) => {
            crossSellingEl.classList.remove("ff-d-none");
            const recommendationCard = this.ffRecommendationCard(product);

            if (recommendationCard) {
              crossSellingEl.appendChild(recommendationCard);
            }
          });
      }

      /**
       *
       * @param {string} productId
       */
      async ffAddCrossSelling(productId) {
        /**
         * @type {HTMLDivElement}
         */
        // @ts-ignore
        const crossSellingEl = this.querySelector(
          "#ff-cart-drawer_recommendations"
        );
        const url = `${crossSellingEl.dataset.url}?product_id=${productId}&intent=complementary&limit=${FfCartDrawer.crossSelling.maxRetrievedProducts}`;
        const response = await fetch(url);
        const { products } = await response.json();
        const crossSellingCards = Array.from(
          crossSellingEl.querySelectorAll(
            "[data-ff-recommendation-add-to-cart]"
          )
        );

        //filter products already in the recommended section
        products
          .filter(({ variants }) => {
            const firstVariantAvailable = variants.find(
              ({ available }) => available == true
            );

            if (firstVariantAvailable) {
              return !crossSellingCards.find(
                (card) => card.dataset.variantId == firstVariantAvailable.id
              );
            }

            return true;
          })
          .forEach((product) => {
            const recommendationCard = this.ffRecommendationCard(product);

            if (recommendationCard) {
              crossSellingEl.classList.remove("ff-d-none");
              crossSellingEl.appendChild(recommendationCard);
            }
          });
      }

      /**
       *
       * @param {{variants: {id:string, price:number, compare_at_price:number, name:string, available:boolean}[], featured_image:string, url:string}} product
       * @returns {FfRecommendationCard | undefined}
       */
      ffRecommendationCard(product) {
        const { featured_image, variants, url } = product;
        const firstVariantAvailable = variants.find(
          ({ available }) => available == true
        );

        if (!firstVariantAvailable) return undefined;
        const { price, name, id, compare_at_price } = firstVariantAvailable;

        const compareAtPriceFormated = Shopify.formatMoney(
          compare_at_price
        ).replace(this.dataset.ffCurrencySymbol, "");

        const priceFormated = Shopify.formatMoney(price).replace(
          this.dataset.ffCurrencySymbol,
          ""
        );

        const complementPrice = () => {
          let result = this.dataset.ffCurrencySymbol + priceFormated;

          if (compare_at_price > 0 && price != compare_at_price) {
            let compare_at_price_formated =
              '<span class="ff-cart-drawer_complement_compare-at-price">' +
              this.dataset.ffCurrencySymbol +
              compareAtPriceFormated +
              "</span> ";

            result = compare_at_price_formated + result + "*";
          }

          return "+ " + result;
        };

        /**
         * @type {FfRecommendationCard}
         */
        // @ts-ignore
        const newEl = document.createElement("ff-recommendation-card");
        newEl.classList.add("ff-cart-drawer_complement");
        newEl.innerHTML = `
            <img
              class="ff-cart-drawer_complement_img"
              src=${featured_image}&width=300
              alt="${name} image"
              loading="lazy"
              width="77"
              height="65"
            >
            <h3 class="ff-cart-drawer_complement_title">
              ${name}
            </h3>
            <button class="ff-cart-drawer_complement_cta" data-ff-recommendation-add-to-cart data-variant-id="${id}">
              ${this.dataset.ffAddRecommendationText}
            </button>
            <span class="ff-cart-drawer_complement_price">
              ${complementPrice()}
            </span>`;

        return newEl;
      }

      /**
       * @param {boolean} value Indicates if the submit button must be enable or disable
       */
      enable(value) {
        if (value) {
          this.querySelector("[data-ff-checkout-button]")?.removeAttribute(
            "disabled"
          );
        } else {
          this.querySelector("[data-ff-checkout-button]")?.setAttribute(
            "disabled",
            "disabled"
          );
        }
      }

      getSectionsToRender = () => {
        return [
          {
            id: "ff-cart-drawer",
          },
          {
            id: "ff-cart-icon-bubble",
          },
        ];
      };

      renderContents = async (parsedState) => {
        let sectionsFetched = parsedState?.sections || {};

        for (const section of this.getSectionsToRender()) {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          let newSectionElement;
          if (sectionsFetched[section.id]) {
            newSectionElement = sectionsFetched[section.id];
          } else {
            newSectionElement = await fetch(
              //@ts-ignore
              Shopify.routes.root + "?section_id=" + section.id
            ).then((res) => res.text());
          }
          if (sectionElement) {
            if (section.id == "ff-cart-drawer") {
              this.renderSelf(newSectionElement);
            } else {
              sectionElement.innerHTML = this.getSectionInnerHTML(
                newSectionElement,
                section.selector
              );
            }
          }
        }
        if (!this.ffIsOpen) {
          document.getElementById("ff-cart-icon-bubble")?.click();
        }
      };

      getSectionInnerHTML = (html, selector = ".shopify-section") => {
        //@ts-ignore
        return new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector).innerHTML;
      };

      /**
       * @param {HTMLElement | Document} lineItemsContainer
       * @returns {{[lineId:string]:HTMLElement}}
       */
      ffLineItemsObj = (lineItemsContainer) => {
        /**
         * @type {HTMLElement[]}
         */
        const lineItemsEL = Array.from(
          lineItemsContainer
            .querySelector("[data-ff-line-items]")
            ?.querySelectorAll("[data-ff-line-item]") || []
        );

        return lineItemsEL.reduce((lineItemsObj, lineItemEL) => {
          const { ffLineId } = lineItemEL.dataset;

          if (ffLineId) {
            lineItemsObj[ffLineId] = lineItemEL;
          }

          return lineItemsObj;
        }, {});
      };

      renderSelf(section) {
        const sectionDOM = new DOMParser().parseFromString(
          section,
          "text/html"
        );

        /**
         * @type {HTMLElement[]}
         */
        const newDrawerItems = Array.from(
          sectionDOM
            .querySelector("[data-ff-line-items]")
            ?.querySelectorAll("[data-ff-line-item]") || []
        );

        const drawerItems = this.querySelector("[data-ff-line-items]");

        if (newDrawerItems.length > 0 && drawerItems) {
          const newTotalCart =
            sectionDOM.querySelector("[data-ff-totals]")?.innerHTML;

          const cartTotalPriceEl = this.querySelector("[data-ff-totals]");

          if (cartTotalPriceEl && newTotalCart) {
            cartTotalPriceEl.innerHTML = newTotalCart;
          }

          /**
           * @type {HTMLElement[]}
           */
          const cartItemsCountELArray = Array.from(
            this.querySelectorAll("[data-ff-line-items-count]")
          );

          const newCartItemsCount = sectionDOM.querySelector(
            "[data-ff-line-items-count]"
          )?.textContent;

          if (newCartItemsCount) {
            cartItemsCountELArray.forEach((cartItemsCountEL) => {
              cartItemsCountEL.textContent = newCartItemsCount;
            });
          }

          //Update and filter of drawer line items
          const drawerItemsObj = this.ffLineItemsObj(this);

          const recommendedProductsEl = this.querySelectorAll(
            "#ff-cart-drawer_recommendations [data-ff-recommendation-add-to-cart]"
          );

          newDrawerItems.forEach((newLineItemEL) => {
            const { ffLineId, ffProductId, variantId } = newLineItemEL.dataset;

            recommendedProductsEl.forEach((recommendedProductEl) => {
              if (recommendedProductEl.dataset.variantId == variantId) {
                recommendedProductEl.closest("ff-recommendation-card").remove();
              }
            });

            if (ffLineId && drawerItemsObj[ffLineId]) {
              const oldLineEL = drawerItemsObj[ffLineId];
              //This content purpose is to avoid line image update and preserve event listeners
              const newLineContentEl = newLineItemEL.querySelectorAll(
                "[data-ff-replaceable-content]"
              );
              const oldLineContentEl = oldLineEL.querySelectorAll(
                "[data-ff-replaceable-content]"
              );

              oldLineContentEl.forEach((el, idx) => {
                el.replaceWith(newLineContentEl[idx]);
              });

              delete drawerItemsObj[ffLineId]; //We will remove elements remaining in drawerItemsObj after the filtering
            } else {
              drawerItems.prepend(newLineItemEL);
              this.ffAddCrossSelling(ffProductId);
            }
          });

          //If all recommended products were removed, hide recommendation container
          if (this.querySelectorAll("ff-recommendation-card").length == 0) {
            this.querySelector("#ff-cart-drawer_recommendations").classList.add(
              "ff-d-none"
            );
          }

          //Removal of line item remaining in drawerItemsObj after the filtering
          Object.values(drawerItemsObj).forEach((remainingLineEl) =>
            remainingLineEl.remove()
          );

          drawerItems.scrollBy({
            top: -drawerItems.scrollHeight,
            behavior: "smooth",
          });
        } else {
          const newDrawerContent = sectionDOM.querySelector(
            "[data-ff-drawer-content]"
          );

          if (newDrawerContent && this.ffDrawerContent) {
            this.ffDrawerContent.innerHTML = newDrawerContent.innerHTML;
            this.ffInit(true);

            if (newDrawerItems.length > 0) {
              this.ffInitCrossSelling();
            }
          }
        }

        if (
          this.ffDrawerContent.querySelectorAll("[data-ff-line-item]").length >
          0
        ) {
          document.getElementById(
            "ff-cart-drawer-trigger_color-indicator"
          ).style.fill = "#282828";
        } else {
          document.getElementById(
            "ff-cart-drawer-trigger_color-indicator"
          ).style.fill = "none";
        }
      }

      // This method only exists to have compatibility with product-form
      setActiveElement() {}
    }
  );
}
if (!customElements.get("ff-cart-item-events-core")) {
  customElements.define(
    "ff-cart-item-events-core",
    class FfCartItem extends HTMLElement {
      /**
       * @type {string}
       * The id associated to the cart line
       */
      lineId;

      /**
       * @type {HTMLDivElement}
       * The cart drawer element parent of this drawer item
       */
      cartDrawer;

      /**
       * @type {number}
       * Store the current quantity for the line, just in case a quantity update couldn't be done
       */
      ffPrevQty;

      constructor() {
        super();
        /**
         * @type {string}
         */
        //@ts-ignore
        this.ffLineId = this.dataset.ffLineId;

        /**
         * @type {string}
         */
        //@ts-ignore
        this.ffLineKey = this.dataset.ffLineKey;

        /**
         * @type {FfCartDrawer}
         */
        //@ts-ignore
        this.ffCartDrawer = this.closest("cart-drawer");
        /**
         * @type {HTMLSelectElement}
         */
        //@ts-ignore
        const quantitySelector = this.closest(
          "[data-ff-line-item]"
        ).querySelector("[data-ff-cart-line-quantity-selector]");

        this.ffPrevQty = parseInt(quantitySelector.value);

        quantitySelector.addEventListener("change", async () => {
          const isLoading = this.classList.contains("loading");

          if (isLoading) return;

          const newQty = parseInt(quantitySelector.value);

          const spinner = ffGetSpinner();
          quantitySelector.parentElement?.appendChild(spinner);

          quantitySelector.classList.add("ff-loading");

          this.classList.add("loading");

          quantitySelector.setAttribute("disabled", "disabled");

          this.ffCartDrawer.enable(false);

          try {
            const res = await fetch(
              window["Shopify"].routes.root + "cart/update.js",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  // @ts-ignore
                  sections: this.ffCartDrawer
                    .getSectionsToRender()
                    .map((section) => section.id),
                  updates: { [this.ffLineKey]: newQty },
                }),
              }
            );

            const result = await res.json();

            ffReleaseSpinner(spinner);

            if (result?.status === 422) {
              quantitySelector.value = this.ffPrevQty.toString();
            } else {
              this.ffPrevQty = newQty;

              await this.ffCartDrawer.renderContents(result);
            }
          } catch (error) {
            console.log(error);

            quantitySelector.value = this.ffPrevQty.toString();
          } finally {
            this.classList.remove("loading");

            quantitySelector.removeAttribute("disabled");

            this.ffCartDrawer.enable(true);

            quantitySelector.classList.remove("ff-loading");

            ffReleaseSpinner(spinner);
          }
        });

        /**
         * @type {HTMLElement}
         */
        //@ts-ignore
        const eliminateLineEl = this.closest(
          "[data-ff-line-item]"
        ).querySelector("[data-ff-cart-line-eliminate]");

        eliminateLineEl?.addEventListener("click", async (e) => {
          eliminateLineEl.classList.add("ff-loading");
          eliminateLineEl.disabled = true;
          const spinner = ffGetSpinner();
          eliminateLineEl.appendChild(spinner);

          this.ffCartDrawer.enable(false);

          try {
            const res = await fetch(
              window["Shopify"].routes.root + "cart/update.js",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  // @ts-ignore
                  sections: this.ffCartDrawer
                    .getSectionsToRender()
                    .map((section) => section.id),
                  updates: { [this.ffLineKey]: 0 },
                }),
              }
            );

            const result = await res.json();

            ffReleaseSpinner(spinner);

            if (result?.status === 422) {
              console.log(result);
            } else {
              this.ffPrevQty = 0;

              await this.ffCartDrawer.renderContents(result);
            }
          } catch (error) {
            console.log(error);

            ffReleaseSpinner(spinner);
          } finally {
            this.ffCartDrawer.enable(true);
            eliminateLineEl.classList.remove("ff-loading");
          }
        });
      }
    }
  );
}


/**
 * Manually refreshes the Shopify Cart Drawer
 */

async function refreshCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  
  if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
    try {
      // We pass null or an empty object because renderContents 
      // will fetch the sections itself if no parsedState is provided.
      await cartDrawer.renderContents({ sections: null });
      console.log('Cart drawer refreshed successfully.');
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  } else {
    console.warn('Cart Drawer element not found or not initialized.');
  }
}
